import { motion, type Variants } from 'framer-motion';
import { Monitor, Smartphone, Zap, Palette, Search, Shield } from 'lucide-react';

const services = [
  {
    icon: Monitor,
    title: 'Custom Website Design',
    description: 'Tailored websites that reflect your brand identity and business goals with unique, memorable design.',
  },
  {
    icon: Smartphone,
    title: 'Responsive Development',
    description: 'Websites that look and perform flawlessly on every device, from mobile phones to desktop monitors.',
  },
  {
    icon: Zap,
    title: 'Performance Optimization',
    description: 'Lightning-fast load times and smooth interactions that keep visitors engaged and improve SEO.',
  },
  {
    icon: Palette,
    title: 'Brand Identity',
    description: 'Cohesive visual systems including color palettes, typography, and design guidelines.',
  },
  {
    icon: Search,
    title: 'SEO Strategy',
    description: 'Search engine optimized structure and content that helps your business get discovered.',
  },
  {
    icon: Shield,
    title: 'Secure Hosting',
    description: 'Reliable, secure hosting solutions with SSL certificates and regular backups.',
  },
];

const container: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const Services = () => {
  return (
    <section id="services" className="py-24 bg-white dark:bg-dark transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-highlight font-medium text-sm uppercase tracking-wider">What We Do</span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-primary dark:text-white mt-3 mb-4">
            Services Built for Growth
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg">
            Comprehensive web solutions designed to strengthen your digital presence and drive business results.
          </p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-100px' }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service) => (
            <motion.div
              key={service.title}
              variants={item}
              whileHover={{ y: -6 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              className="group p-8 rounded-2xl bg-gray-50 dark:bg-secondary/40 border border-transparent dark:border-white/5 hover:bg-primary dark:hover:bg-primary transition-colors duration-300"
            >
              <div className="w-14 h-14 bg-primary/10 dark:bg-white/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors">
                <service.icon className="text-primary dark:text-highlight group-hover:text-white transition-colors" size={28} />
              </div>
              <h3 className="text-xl font-bold text-primary dark:text-white group-hover:text-white mb-3 transition-colors">
                {service.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 group-hover:text-white/80 transition-colors leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
